import React from 'react';
import { View, Pressable } from 'react-native-web';
import CustomText from '../atoms/CustomText';
import { colors, borderRadius, spacing } from '../../utils/styling';
import { useResponsive } from '../../hooks/useResponsive';

interface Option {
  text: string;
  category?: string;
}

interface PersonalityQuestionProps {
  question: string;
  value?: number;
  onChange?: (value: number) => void;
  id?: string;
  options?: Option[];
  shuffle?: boolean; // whether to shuffle option order; default true
  helper?: string; // optional sub-line under the question
}

// Fisher-Yates shuffle algorithm
const shuffleArray = <T,>(array: T[]): T[] => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

const PersonalityQuestion: React.FC<PersonalityQuestionProps> = ({ 
  question, 
  value, 
  onChange, 
  id,
  options = [],
  shuffle = true,
  helper,
}) => {
  const { isMobile } = useResponsive();
  // Create array of indices and shuffle them
  const [shuffledIndices] = React.useState(() =>
    shuffle
      ? shuffleArray(Array.from({ length: options.length }, (_, i) => i))
      : Array.from({ length: options.length }, (_, i) => i)
  );

  // Map the selected value to the original index
  const handleOptionSelect = (shuffledIndex: number) => {
    const originalIndex = shuffledIndices[shuffledIndex];
    onChange?.(originalIndex);
  };

  // Map the current value to the shuffled index
  const getShuffledValue = () => {
    if (value === undefined) return undefined;
    return shuffledIndices.indexOf(value);
  };

  return (
    <View id={id} style={{ 
      marginTop: isMobile ? spacing.md : spacing.lg,
      width: '100%',
    }}>
      <CustomText 
        variant="h2" 
        align="center" 
        style={{ 
          fontSize: isMobile ? 20 : 24, 
          fontWeight: 700, 
          color: 'black', 
          fontFamily: "Nunito",
          paddingHorizontal: isMobile ? spacing.md : 0,
          textAlign: 'left',
          marginLeft: isMobile ? spacing.md : spacing.xl,
        }}
      >
        {question}
      </CustomText>
      {helper ? (
        <CustomText
          style={{
            fontSize: isMobile ? 14 : 16,
            fontStyle: 'italic',
            color: colors.gray[600],
            fontFamily: "Nunito",
            textAlign: 'left',
            marginTop: spacing.xs,
            marginLeft: isMobile ? spacing.md : spacing.xl,
            paddingHorizontal: isMobile ? spacing.md : 0,
          }}
        >
          {helper}
        </CustomText>
      ) : null}
      <View style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        gap: isMobile ? spacing.xs : spacing.sm,
        width: '100%',
        marginTop: isMobile ? spacing.md : spacing.lg,
        paddingHorizontal: isMobile ? spacing.xs : 0
      }}>
        {shuffledIndices.map((originalIndex, shuffledIndex) => {
          const option = options[originalIndex];
          const isSelected = getShuffledValue() === shuffledIndex;
          return (
            <Pressable
              key={shuffledIndex}
              onPress={() => handleOptionSelect(shuffledIndex)}
              style={{
                backgroundColor: isSelected ? colors.primary : colors.white,
                boxShadow: isSelected
                  ? '0 4px 24px 0 rgba(30, 41, 59, 0.18)'
                  : undefined,
                borderRadius: borderRadius.lg,
                padding: isMobile ? spacing.sm : spacing.md,
                fontFamily: "Nunito",
                fontWeight: isSelected ? 700 : 400,
                cursor: 'pointer',
                transition: 'all 0.2s',
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: isMobile ? spacing.sm : spacing.md,
                marginBottom: isMobile ? spacing.xs : spacing.sm,
                position: 'relative',
              }}
              onMouseEnter={(e: React.MouseEvent<HTMLDivElement>) => {
                e.currentTarget.style.boxShadow = '0 4px 24px 0 rgba(30, 41, 59, 0.12)';
              }}
              onMouseLeave={(e: React.MouseEvent<HTMLDivElement>) => {
                e.currentTarget.style.boxShadow = isSelected
                  ? '0 4px 24px 0 rgba(30, 41, 59, 0.18)'
                  : '';
              }}
            >
              <View style={{
                width: isMobile ? 20 : 24,
                height: isMobile ? 20 : 24,
                borderRadius: isMobile ? 10 : 12,
                backgroundColor: isSelected ? colors.white : 'transparent',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: isSelected ? '0 2px 8px 0 rgba(30, 41, 59, 0.18)' : undefined,
                transition: 'all 0.2s',
              }}>
                <View style={{
                  width: isMobile ? 16 : 20,
                  height: isMobile ? 16 : 20,
                  borderRadius: isMobile ? 8 : 10,
                  borderWidth: 2,
                  borderStyle: 'solid',
                  borderColor: isSelected ? colors.primary : colors.gray[500],
                  backgroundColor: isSelected ? colors.primary : 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: isSelected ? '0 2px 8px 0 rgba(30, 41, 59, 0.18)' : undefined,
                  transition: 'all 0.2s',
                }}>
                  {isSelected && (
                    <View style={{
                      width: isMobile ? 8 : 10,
                      height: isMobile ? 8 : 10,
                      borderRadius: isMobile ? 4 : 5,
                      backgroundColor: colors.white,
                      boxShadow: '0 1px 4px 0 rgba(30, 41, 59, 0.18)',
                    }} />
                  )}
                </View>
              </View>
              <CustomText style={{ 
                color: isSelected ? colors.white : colors.gray[700],
                fontSize: isMobile ? 14 : 18,
                fontWeight: isSelected ? '700' : '400',
                textShadow: isSelected
                  ? '0 2px 8px rgba(30, 41, 59, 0.18)'
                  : undefined,
                transition: 'all 0.2s',
                fontFamily: "Nunito",
                flex: 1,
                paddingRight: isMobile ? spacing.sm : 0
              }}>
                {option.text}
              </CustomText>
            </Pressable>
          );
        })}
      </View>
    </View>
  );
};

export default PersonalityQuestion; 